// ============================================================================
// PROMPT ASSEMBLY HELPER
// ----------------------------------------------------------------------------
// Replaces the scattered `.replace('{PLACEHOLDER}', value)` pattern with a
// single entry point that:
//   1. Takes a prompt template + a replacements object
//   2. Substitutes every {PLACEHOLDER} token in the template
//   3. Warns (via console + returned metadata) when a placeholder is missing
//      or when a supplied replacement key has no matching token
//   4. Normalises null/undefined to empty string so malformed replacements
//      don't inject "undefined" into prompts
//
// Example:
//   assemblePrompt(ANALYSIS_PROMPT, {
//     LOCALITY_BLOCK: formatLocalityBlock(locality),
//     APPLICABLE_STANDARDS: formatStandardsPackBlock(pack),
//     PRICING_REFERENCE: pricingBlock,
//     CONTEXT: fullContext,
//   })
//
// Supports nested `{key.subKey}` access for objects (not used heavily but
// handy for passing structured config).
// ============================================================================

/**
 * Coerce any replacement value to a prompt-safe string.
 *   string  → as-is
 *   number  → String(n) (including 0)
 *   boolean → 'true' / 'false'
 *   array   → items joined with newlines (most prompt-friendly form)
 *   object  → pretty-printed JSON
 *   null    → empty string (treated as "intentionally blank")
 */
function coerceReplacement(raw) {
    if (raw == null) return '';
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
    if (Array.isArray(raw)) return raw.map(item => coerceReplacement(item)).join('\n');
    if (typeof raw === 'object') {
        try {
            return JSON.stringify(raw, null, 2);
        } catch {
            return '[unserializable object]';
        }
    }
    return String(raw);
}

/**
 * Substitute {PLACEHOLDER} tokens in a prompt template.
 *
 * @param {string} template - the prompt template with {PLACEHOLDER} tokens
 * @param {Object} replacements - map of token → string | number | boolean | array | object
 * @param {Object} [opts]
 * @param {boolean} [opts.strict=false] - throw if any token is left unsubstituted
 * @param {boolean} [opts.warn=true] - console.warn on missing tokens / extras
 * @param {boolean} [opts.preserveUnknown=false] - leave `{TOKEN}` literals in place
 *        for tokens not present in replacements (so a later pass can substitute them).
 *        Takes priority over the default "(missing marker)" behaviour.
 * @returns {Object} { prompt, substituted, missing, extras, tokensInTemplate }
 */
export function assemblePrompt(template, replacements = {}, opts = {}) {
    const { strict = false, warn = true, preserveUnknown = false } = opts;

    if (typeof template !== 'string') {
        throw new TypeError('assemblePrompt: template must be a string');
    }

    const tokenRegex = /\{([A-Z][A-Z0-9_.]*)\}/g;
    const tokensInTemplate = new Set();
    let m;
    while ((m = tokenRegex.exec(template)) !== null) {
        tokensInTemplate.add(m[1]);
    }

    const substituted = [];
    const missing = [];

    // Resolve a dotted key against the replacements map (e.g. "LOCALITY.STATE"
    // walks replacements.LOCALITY.STATE).
    const resolveValue = (key) => {
        if (key in replacements) return replacements[key];
        if (key.includes('.')) {
            const parts = key.split('.');
            let cursor = replacements[parts[0]];
            for (let i = 1; i < parts.length; i++) {
                if (cursor == null) return undefined;
                cursor = cursor[parts[i]];
            }
            return cursor;
        }
        return undefined;
    };

    // ⚠️  SINGLE-PASS substitution.
    //
    // The previous implementation iterated tokensInTemplate and did a
    // `result.split(...).join(...)` for each. Because each iteration
    // operated on the string produced by the PREVIOUS iteration, a
    // curly-brace sequence inside a value (e.g. a bio field containing
    // "See {BUSINESS_RULES}") could be picked up by a LATER iteration's
    // substitution and replaced with whatever sat under that key in the
    // replacements map. That's a prompt-injection vector - malicious or
    // accidental - that corrupts the final prompt.
    //
    // The fix: do ALL substitutions in ONE regex replace pass. The
    // callback receives each matched `{TOKEN}` from the TEMPLATE, looks
    // up its value, and returns the coerced string. That return value is
    // NOT re-scanned, so curly braces inside values remain literal text.
    //
    // Additionally: we escape any regex-metacharacters in token names
    // (shouldn't appear in practice since the token regex is [A-Z0-9_.],
    // but defensive in case the spec ever widens).
    const tokensArr = Array.from(tokensInTemplate);
    let result;
    if (tokensArr.length === 0) {
        result = template;
    } else {
        const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const combinedPattern = new RegExp(
            `\\{(${tokensArr.map(escapeRegex).join('|')})\\}`,
            'g',
        );
        result = template.replace(combinedPattern, (_match, token) => {
            const raw = resolveValue(token);
            if (raw === undefined) {
                if (!missing.includes(token)) missing.push(token);
                if (strict) return _match;           // keep literal; strict mode throws after the loop
                if (preserveUnknown) return _match;  // leave for a downstream pass
                return `(${token.toLowerCase().replace(/_/g, ' ')} not provided)`;
            }
            if (!substituted.includes(token)) substituted.push(token);
            return coerceReplacement(raw);
        });
    }

    // Detect extras: keys supplied but not present in template
    const extras = [];
    for (const key of Object.keys(replacements)) {
        if (!tokensInTemplate.has(key)) {
            extras.push(key);
        }
    }

    if (warn && missing.length > 0) {
        console.warn(`[assemblePrompt] ${missing.length} placeholder(s) in template were not supplied:`, missing);
    }

    if (strict && missing.length > 0) {
        const err = new Error(`assemblePrompt: strict mode - missing replacements for ${missing.join(', ')}`);
        err.missing = missing;
        throw err;
    }

    return {
        prompt: result,
        substituted,
        missing,
        extras,
        tokensInTemplate: Array.from(tokensInTemplate),
    };
}

